import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import InfoCard from "../../../components/components/js/InfoCard";
import { Masonry } from "masonic";
import { FaUserCircle } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import AuthContext from "../../../context/AuthProvider";
import RoomsCard from "../../../components/components/js/RoomsCard";
import { DeleteConfirmationDialog } from "../../../components/components/js/DeleteConfirmationDialog";
import { Avatar, Typography, Box, Button, IconButton } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from "react-i18next";
import { formatAge, formatDate } from "../../../utils/exportUtils";
import StringListCard from "../../../components/components/js/Stringlistcard";
// import { toast } from "react-toastify";

// GET /api/core/genders/ and GET /api/nurseries/staff/status-choices/ don't
// have saved response examples in either Postman collection, so their exact
// shape is unconfirmed. This normalizes a handful of likely shapes
// (bare array, {results: [...]}, [value, label] pairs, {value,label} objects,
// plain strings) into {id, name} options. If dropdowns show blank/wrong
// labels, log the raw response once and tighten this to match.
const normalizeChoices = (raw) => {
  const list = Array.isArray(raw)
    ? raw
    : raw?.results ?? raw?.choices ?? raw?.status_choices ?? raw?.genders ?? [];

  return list.map((item) => {
    if (Array.isArray(item)) {
      return { id: item[0], name: item[1] ?? item[0] };
    }
    if (typeof item === "string") {
      return { id: item, name: item };
    }
    return {
      id: item.id ?? item.value ?? item.code,
      name: item.name ?? item.label ?? item.display_name ?? item.Name ?? item.value,
    };
  });
};

export const StaffDetails = () => {
  const { staffId } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const { school, auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [cardData, setCardData] = useState([]);
  const fileInputRef = useRef();
  const [personalInfo, setPersonalInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);

      const [staffRes, gendersRes, statusChoicesRes] = await Promise.allSettled([
        axiosPrivate.get(API_URLS.getStaffDetails(school.id, staffId)),
        axiosPrivate.get(API_URLS.gender),
        axiosPrivate.get(API_URLS.staffStatusChoices()),
      ]);

      // The staff lookup is the only one that should fail the whole page.
      if (staffRes.status === "rejected") throw staffRes.reason;

      // Confirmed against a real GET /api/nurseries/{nurseryId}/staff/{staffId}/
      // response: it's a flat object (no {staff, classes} wrapper), with
      // rooms as an array of {id, name} on the top level.
      const staffData = staffRes.value.data;
      const roomsData = staffData.rooms ?? [];

      const currentRooms = roomsData.map((room) => ({
        id: room.id,
        room: room.name ?? room.ClassName,
      }));

      const genders =
        gendersRes.status === "fulfilled" ? normalizeChoices(gendersRes.value.data) : [];
      const statusChoices =
        statusChoicesRes.status === "fulfilled"
          ? normalizeChoices(statusChoicesRes.value.data)
          : [
              { id: "Active", name: "Active" },
              { id: "Inactive", name: "Inactive" },
              { id: "Past", name: "Past" },
            ];

      const last_name = staffData.last_name ?? "";
      const name = staffData.first_name + " " + last_name;

      setPersonalInfo({
        name: name,
        image: staffData.profile_img,
      });

      const personal = [
        { label: "First Name", value: staffData.first_name || "", editable: true },
        { label: "Last Name", value: staffData.last_name || "", editable: true },
        {
          label: "BirthDate",
          value: staffData.birth_date,
          type: "date",
          editable: true,
        },
        {
          label: "Age",
          value: formatAge(staffData.birth_date),
          editable: false,
        },
        { label: "Email", value: staffData.email || "", editable: true },
        { label: "Phone", value: staffData.phone, editable: true },
        {
          label: "Gender",
          value: staffData.gender,
          type: "select",
          options: genders,
          editable: true,
        },
        { label: "Allergies", value: staffData.allergies, editable: true },
        { label: "Notes", value: staffData.notes, editable: true },
        { label: "Medications", value: staffData.medications, editable: true },
        { label: "Doctor Name", value: staffData.doctor_name, editable: true },
        {
          label: "Doctor Phone No.",
          value: staffData.doctor_phone,
          editable: true,
        },
      ];

      const employmentInfo = [
        {
          label: "Status",
          value: staffData.status,
          type: "select",
          options: statusChoices,
          editable: true,
        },
        { label: "Role", value: staffData.job_title, editable: true },
        {
          label: "Hire Date",
          value: formatDate(staffData.hire_date),
          editable: true,
          type: "date",
        },
        {
          label: "Employment Notes",
          value: staffData.emp_notes,
          editable: true,
        },
      ];

      const emergencyContact = [
        {
          label: "Emergency Name",
          value: staffData.emergency_contact_name,
          editable: true,
        },
        {
          label: "Emergency Type",
          value: staffData.emergency_contact_type,
          editable: true,
        },
        {
          label: "Emergency Phone",
          value: staffData.emergency_contact_phone,
          editable: true,
        },
      ];

      const address = [
        { label: "Street", value: staffData.street, editable: true },
        { label: "Appartment", value: staffData.apartment_house, editable: true },
        { label: "City", value: staffData.city, editable: true },
        { label: "State", value: staffData.state, editable: true },
        { label: "Zip Code", value: staffData.zip_code, editable: true },
      ];

      const finalData = [
        { type: "info", title: "Personal Info", data: personal },
        { type: "info", title: "Employment Info", data: employmentInfo },
        {
          type: "rooms",
          initialSelected: currentRooms,
          fullWidth: true,
        },
        { type: "info", title: "Emergency Contact", data: emergencyContact },
        { type: "info", title: "Address", data: address, fullWidth: true },
        {
          type: "list",
          title: t("staff_lists.education"),
          field: "education",
          items: Array.isArray(staffData.education) ? staffData.education : [],
        },
        {
          type: "list",
          title: t("staff_lists.certifications"),
          field: "certifications",
          items: Array.isArray(staffData.certifications) ? staffData.certifications : [],
        },
      ];

      setCardData(finalData);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        console.error("Error fetching data", err);
        setError("Failed to load student data.");
      }
    } finally {
      setLoading(false);
    }
  }, [staffId, auth.id, axiosPrivate, school.id]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      await axiosPrivate.delete(API_URLS.getStaffDetails(school.id, staffId));
      navigate(-1);
    } catch (error) {
      console.error("Delete request error:", error);
    } finally {
      setLoading(false);
    }
  };

    const saveList = (field) => async (list) => {
    await axiosPrivate.patch(API_URLS.getStaffDetails(school.id, staffId), {
      [field]: list,
    });
    await fetchAllData();
  };

  const updateImage = async (img) => {
      try {
        setLoading(true);
        setError(null);
        const formData = new FormData();
    formData.append("profile_img", img); 
        const response = await axiosPrivate.patch(
          API_URLS.getStaffDetails(school.id, staffId),
          formData,
          {headers: { "Content-Type": "multipart/form-data" } }
        );
        console.log("Patch success", response);
        navigate(0);
      } catch (err) {
        console.error("Patch error", err);
        setError("Failed to update. Please try again.");
      } finally {
        setLoading(false);
      }
    };

  const handleEditClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.readAsDataURL(file);

        updateImage(file);
    }
  };

  const fullWidthCards = cardData.filter((card) => card.fullWidth);
  const masonryCards = cardData.filter((card) => !card.fullWidth);


    if (notFound) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="80vh"
          textAlign="center"
        >
          <Typography variant="h4" color="primary" gutterBottom>
            {t(`staffDetails.notFound.title`)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t(`staffDetails.notFound.description`)}
          </Typography>
        </Box>
      );
    }

  return (
    <div
      className={`p-5 d-block ms-auto px-4`}
      style={{
        minHeight: "70vh",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: "5%", pr: "35px" }}
      >
        <Box display="flex" alignItems="center" gap={2}>
      <Box position="relative" display="inline-block">
        <Box

          sx={(theme)=>({
            position: "absolute",
            top: -4,
            left: -4,
            width: 93,
            height: 93,
            borderRadius: "50%",
            border: `1px solid ${theme.palette.primary.main}`, 
            pointerEvents: "none", 
          })}
        />
        {personalInfo.image ? (
          <Avatar
            src={personalInfo.image}
            alt="img"
            sx={{ width: 85, height: 85 }}
          />
        ) : (
          <FaUserCircle size={85} />
        )}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <IconButton
          size="small"
          sx={(theme)=>({
            position: "absolute",
            bottom: 0,
            right: 0,
            width:25,
            height:25,
            backgroundColor: "white",
            borderRadius: "50%",
            boxShadow: 1,
            border: `1px solid ${theme.palette.primary.main}`,
            "&:hover": { backgroundColor: "#e3f2fd" },
          })}
          aria-label="edit"
        >
          <EditIcon fontSize="small" color="primary" onClick={handleEditClick}/>
        </IconButton>
      </Box>

      <Typography variant="h5" fontWeight="bold">
        {personalInfo?.name}
      </Typography>
    </Box>

        <Button
          variant="text"
          onClick={() => setShowDeleteConfirm(true)}
          sx={{
            color: "red",
            fontWeight: "bold",
            fontSize:"1.1rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          {t(`staffDetails.deleteButton`)}
        </Button>
      </Box>

      <div style={{ padding: "35px" }}>
        <Masonry
          items={masonryCards}
          columnGutter={18}
          columnWidth={400}
          render={({ data }) => (
            <div>
              {data.type === "info" ? (
                <InfoCard
                  title={data.title}
                  editable
                  data={data.data}
                  id={staffId}
                  type={"staff"}
                  onSaveSuccess={fetchAllData}
                />
              ) : data.type === "list" ? (
                <StringListCard
                  title={data.title}
                  items={data.items}
                  onSave={saveList(data.field)}
                />
              ) : (
                <RoomsCard
                  roomsApiUrl={API_URLS.getSchoolRooms(school.id)}
                  axiosPrivate={axiosPrivate}
                  initialSelected={data.initialSelected}
                  id={staffId}
                  nurseryId={school.id}
                  type="staff"
                  editable
                  onSave={fetchAllData}
                />
              )}
            </div>
          )}
        />
        {fullWidthCards.map((data, idx) => (
          <div key={`full-${idx}`}>
            {data.type === "rooms" ? (
              <RoomsCard
                roomsApiUrl={API_URLS.getSchoolRooms(school.id)}
                axiosPrivate={axiosPrivate}
                initialSelected={data.initialSelected}
                id={staffId}
                nurseryId={school.id}
                type="staff"
                editable
                onSave={fetchAllData}
              />
            ) : (
              <InfoCard
                title={data.title}
                editable
                data={data.data}
                id={staffId}
                type={"staff"}
                onSaveSuccess={fetchAllData}
              />
            )}
          </div>
        ))}
        {showDeleteConfirm && (
          <DeleteConfirmationDialog
            onDelete={handleDelete}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
      </div>
    </div>
  );
};